/**
 * AuthProvider — singleton auth lifecycle owner.
 *
 * Mounted ONCE in App.tsx. Owns the single supabase.auth.onAuthStateChange
 * subscription and the initial getSession() call. Dispatches all auth state
 * into the Redux store so that useAuth (and any other consumer) can read it
 * as a pure selector without creating duplicate subscriptions.
 *
 * This replaces the useEffect that previously lived inside useAuth.ts,
 * which was instantiated 4-6+ times per page and created redundant
 * listeners, profile fetches, and cache-clear cycles.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  setSession,
  setProfile,
  setProfileError,
  clearAuth,
  setPasswordRecovery,
} from '@/store/slices/authSlice';
import { setCurrentCompany } from '@/store/slices/companySlice';
import { clearUserSpecificCaches, hasUserChanged } from '@/utils/authCacheUtils';

export function AuthProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  // Track previous user ID to detect user changes
  const previousUserIdRef = useRef<string | undefined>(undefined);

  // Deduplication guard: prevent concurrent profile fetches
  const profileFetchInFlightRef = useRef(false);

  useEffect(() => {
    // Defense-in-depth: detect recovery mode from URL hash in case the
    // SDK event fires before this listener is registered.
    try {
      const hash = window.location.hash.substring(1);
      const hashParams = new URLSearchParams(hash);
      if (hashParams.get('type') === 'recovery') {
        dispatch(setPasswordRecovery(true));
      }
    } catch {
      // Ignore malformed hash — non-critical path
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const newUserId = session?.user?.id ?? null;

        // Clear user-specific caches when switching between different users
        if (hasUserChanged(previousUserIdRef.current, newUserId)) {
          clearUserSpecificCaches(queryClient);
        }

        previousUserIdRef.current = newUserId;

        // Handle PASSWORD_RECOVERY event
        if (event === 'PASSWORD_RECOVERY') {
          dispatch(setPasswordRecovery(true));
          dispatch(setSession({ session, user: session?.user ?? null }));
          return;
        }

        dispatch(setSession({ session, user: session?.user ?? null }));

        if (session?.user && session.access_token) {
          const now = Date.now() / 1000;
          const expiresAt = session.expires_at || 0;

          if (expiresAt > now) {
            // Session is valid — fetch profile + company
            setTimeout(async () => {
              if (profileFetchInFlightRef.current) return;
              profileFetchInFlightRef.current = true;

              try {
                const { data: profileData, error: profileError } = await supabase
                  .from('profiles')
                  .select('*')
                  .eq('user_id', session.user.id)
                  .single();

                if (profileError) {
                  if (
                    profileError.message?.includes('Failed to fetch') ||
                    profileError.message?.includes('NetworkError')
                  ) {
                    console.warn(
                      'Profile fetch transient error (will retry on next auth event):',
                      profileError.message,
                    );
                  } else {
                    console.error('Profile fetch error:', profileError);
                  }

                  if (
                    profileError.code === 'PGRST301' ||
                    profileError.message?.includes('permission')
                  ) {
                    console.log('Permission error detected, signing out');
                    dispatch(clearAuth());
                    await supabase.auth.signOut();
                    return;
                  }

                  dispatch(setProfileError(profileError.message || 'Failed to load profile'));
                  dispatch(setProfile(null));
                  return;
                }

                if (profileData) {
                  dispatch(setProfile(profileData));

                  if (profileData.company_id) {
                    try {
                      const { data: companyData, error: companyError } = await supabase
                        .from('companies')
                        .select('*')
                        .eq('id', profileData.company_id)
                        .single();

                      if (companyData && !companyError) {
                        dispatch(setCurrentCompany(companyData));
                      }
                    } catch (companyFetchError) {
                      console.error('Error fetching company data:', companyFetchError);
                    }
                  }
                }
              } catch (error) {
                console.error('Unexpected error fetching profile:', error);
                dispatch(clearAuth());
              } finally {
                profileFetchInFlightRef.current = false;
              }
            }, 0);
          } else {
            console.log('Session expired, clearing auth');
            dispatch(clearAuth());
            await supabase.auth.signOut();
          }
        } else {
          dispatch(clearAuth());
        }
      },
    );

    // Check for existing session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      dispatch(setSession({ session, user: session?.user ?? null }));
    });

    return () => subscription.unsubscribe();
  }, [dispatch, queryClient]);

  return <>{children}</>;
}
